import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { User, UserCheck, Link, Loader2 } from "lucide-react";
import { PatientLinker } from "@/components/smartfile/PatientLinker";

interface PrescriptionPatientLinkerProps {
  suggestedName?: string;
  suggestedCPF?: string;
  confidence?: number;
  onPatientLinked: (patientId: string) => void;
  currentPatientId: string | null;
}

export function PrescriptionPatientLinker({
  suggestedName,
  suggestedCPF,
  confidence = 0,
  onPatientLinked,
  currentPatientId
}: PrescriptionPatientLinkerProps) {
  const [showLinker, setShowLinker] = useState(false);
  const hasAutoIdentification = !!suggestedName || !!suggestedCPF;

  const getConfidenceBadge = () => {
    if (confidence >= 0.8) {
      return <Badge className="bg-green-100 text-green-700 border-green-300">Alta Confiança</Badge>;
    } else if (confidence >= 0.5) {
      return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300">Média Confiança</Badge>;
    } else {
      return <Badge className="bg-orange-100 text-orange-700 border-orange-300">Baixa Confiança</Badge>;
    }
  };

  return (
    <Card className={currentPatientId ? "border-green-200 bg-green-50/30" : "border-border"}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {currentPatientId ? (
            <>
              <UserCheck className="h-4 w-4 text-green-600" />
              <span className="text-green-700">Paciente Vinculado</span>
            </>
          ) : hasAutoIdentification ? (
            <>
              <User className="h-4 w-4 text-teal-600" />
              <span className="text-teal-700">Paciente Identificado</span>
            </>
          ) : (
            <>
              <User className="h-4 w-4 text-muted-foreground" />
              <span>Selecionar Paciente</span>
            </>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!currentPatientId && hasAutoIdentification && (
          <div className="p-4 rounded-lg bg-white border border-teal-200 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-teal-900">Identificação Automática</p>
              {getConfidenceBadge()}
            </div>
            <div className="space-y-1">
              {suggestedName && (
                <p className="text-sm">
                  <span className="font-semibold">Nome:</span> {suggestedName}
                </p>
              )}
              {suggestedCPF && (
                <p className="text-sm">
                  <span className="font-semibold">CPF:</span> {suggestedCPF}
                </p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              ⚠️ Revise e confirme a identificação antes de prosseguir.
            </p>
          </div>
        )}

        {!currentPatientId ? (
          <PatientLinker
            suggestedName={suggestedName}
            suggestedCPF={suggestedCPF}
            onPatientLinked={onPatientLinked}
            isChangingPatient={false}
          />
        ) : (
          <div className="flex items-center justify-between p-4 rounded-lg bg-green-50 border border-green-200">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-900">Paciente confirmado</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowLinker(true)}
              className="border-green-300 text-green-700 hover:bg-green-100"
            >
              <Link className="h-4 w-4 mr-2" />
              Trocar Paciente
            </Button>
          </div>
        )}

        {showLinker && currentPatientId && (
          <div className="mt-4">
            <PatientLinker
              onPatientLinked={(newId) => {
                onPatientLinked(newId);
                setShowLinker(false);
              }}
              isChangingPatient={true}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
